/*
| The market catalog: identity, addressing and the list rows.
|
| Every market comes from Monaco's own catalog (/market/pairs), so its symbol,
| mode, category and decimals are stated facts. Monaco lists some symbols twice
| (BTC/USDC is both SPOT and MARGIN): a bare symbol URL resolves to the perp,
| because that is the one carrying mark price, funding and open interest, and
| the spot twin is addressed by its uuid.
*/

import { toNumber } from './values.js';

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** The URL segment for a symbol: 'BTC/USDC' becomes 'BTC-USDC'. */
export function marketKey(pair) {
  return String(pair.symbol).replaceAll('/', '-');
}

export function pairsById(pairs) {
  return new Map((pairs ?? []).map((pair) => [pair.id, pair]));
}

/** The pair a URL segment names: a uuid exactly, a symbol preferring MARGIN. */
export function resolveMarket(pairs, key) {
  if (!key) return null;
  const list = pairs ?? [];

  if (UUID.test(key)) {
    return list.find((pair) => pair.id.toLowerCase() === key.toLowerCase()) ?? null;
  }

  const symbol = key.replaceAll('-', '/').toUpperCase();
  const matches = list.filter((pair) => String(pair.symbol).toUpperCase() === symbol);
  return matches.find((pair) => pair.marketType === 'MARGIN') ?? matches[0] ?? null;
}

/** The same symbol listed in the other mode, if Monaco lists it twice. */
export function siblingOf(pairs, pair) {
  return (pairs ?? []).find((other) => other.symbol === pair.symbol && other.marketType !== pair.marketType) ?? null;
}

/** The detail URL for a pair: its symbol unless that symbol resolves to its twin. */
export function marketHref(pairs, pair) {
  const bySymbol = resolveMarket(pairs, marketKey(pair));
  return `/markets/${bySymbol?.id === pair.id ? marketKey(pair) : pair.id}`;
}

export const modeLabel = (marketType) => (marketType === 'MARGIN' ? 'Perp' : 'Spot');

/** One row of the markets table: the catalog entry joined to its screener figures. */
export function marketRows(pairs, screenerItems) {
  const screener = new Map((screenerItems ?? []).map((item) => [item.tradingPairId, item]));

  const rows = (pairs ?? []).map((pair) => {
    const item = screener.get(pair.id) ?? {};
    const isPerp = pair.marketType === 'MARGIN';

    return {
      id: pair.id,
      // Per listing: the spot and perp twins share a symbol, not a star.
      watchId: pair.id,
      key: marketKey(pair),
      href: marketHref(pairs, pair),
      symbol: pair.symbol,
      baseToken: pair.baseToken,
      mode: pair.marketType,
      modeLabel: modeLabel(pair.marketType),
      isPerp,
      category: pair.category || null,
      lastPrice: item.lastPrice ?? null,
      volume24hUsd: item.quoteVolume24h ?? null,
      volumeTotalUsd: item.totalQuoteVolumeLtd ?? null,
      tradesTotal: item.totalTradeCountLtd ?? null,
      // Spot settles at the trade: nothing stays open and nothing is levered.
      openInterestUsd: isPerp ? (item.openInterestNotional ?? null) : null,
      maxLeverage: isPerp ? toNumber(pair.maxLeverage) : null,
    };
  });

  return rows.sort((a, b) => {
    const va = toNumber(a.volume24hUsd);
    const vb = toNumber(b.volume24hUsd);
    if (va === null && vb === null) return a.symbol.localeCompare(b.symbol);
    if (va === null) return 1;
    if (vb === null) return -1;
    return vb - va;
  });
}

export function matchesFilter(row, key) {
  switch (key) {
    case 'spot': return row.mode === 'SPOT';
    case 'perp': return row.mode === 'MARGIN';
    case 'crypto':
    case 'equities':
    case 'commodities':
    case 'fx': return row.category === key;
    default: return true;
  }
}
